import { controllableApplicationTypes } from 'api-validations/stitch/applications'
import { z } from 'zod'

export const GEOJSON_COORDINATE_PRECISION = 7

/**
 * A 2D position represented as [longitude, latitude], aka [easting, northing]
 */
const position2DSchema = z.array(z.number()).length(2).describe('2D position')

/**
 * intentionally unused but here to document that some libraries may
 * account for 3D coordinates in their types, and they may do it by using
 * an array for position instead of a tuple: [longitude, latitude, altitude]
 */
const _position3DSchema = z.array(z.number()).length(3).describe('3D position')

const isClosedRing = (ring: number[][]) => {
  const first = ring.at(0)
  const last = ring.at(-1)
  return first !== undefined && last !== undefined && first[0] === last[0] && first[1] === last[1]
}

/**
 * A closed line of coordinates that defines a polygon shape.
 * First and last coordinates must be the same.
 */
const linearRingSchema = z
  .array(position2DSchema)
  .min(4)
  .refine(isClosedRing, { message: 'First and last positions of a linear ring must be the same' })

/**
 * A polygon is a linear ring with 0 or more inner linear rings.
 * The first linear ring is the outer ring, and the rest are holes.
 */
const polygonCoordinatesSchema = z.array(linearRingSchema).min(1)

/**
 * A multi-polygon is an array of polygons.
 * See polygonCoordinatesSchema for the definition of a polygon.
 */
const multiPolygonCoordinatesSchema = z.array(polygonCoordinatesSchema).min(1)

export const lineStringSchema = z.object({
  type: z.literal('LineString'),
  coordinates: z.array(position2DSchema).min(2)
})

export const pointSchema = z.object({
  type: z.literal('Point'),
  coordinates: position2DSchema
})

export const polygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: polygonCoordinatesSchema
})

export const multiPolygonSchema = z.object({
  type: z.literal('MultiPolygon'),
  coordinates: multiPolygonCoordinatesSchema
})

/**
 * GeoJSON properties can be any value, but for our purposes,
 * we only care about numeric properties. This is a narrowed
 * version of the spec.
 */
export const numericPropertiesSchemaWithZone = z
  .object({
    zone: z.union([z.string(), z.number()]).optional(),
    name: z.union([z.string(), z.number()]).optional()
  })
  .catchall(z.number())
  .nullable()

export const featureSchema = <TOptions extends { geometry?: z.ZodType; properties?: z.ZodType }>(
  options: TOptions
) =>
  z.object({
    id: z.string().or(z.number()).optional(),
    type: z.literal('Feature'),
    ...options
  })

export const featureCollectionSchema = <TFeatureSchema extends z.ZodType>(
  featureSchema: TFeatureSchema
) => z.object({ type: z.literal('FeatureCollection'), features: z.array(featureSchema) })

export const rateZoneFeatureSchema = featureSchema({
  geometry: z.discriminatedUnion('type', [polygonSchema, multiPolygonSchema]),
  properties: numericPropertiesSchemaWithZone
})

export const rateZoneFeatureCollection = featureCollectionSchema(rateZoneFeatureSchema)

export const rxSchema = z.object({
  prescription: rateZoneFeatureCollection,
  /**
   * Implicitly determines the unit of measure for the application rate,
   * e.g., 'gal/ac' for liquid, 'lbs/ac' for granular.
   */
  type: controllableApplicationTypes,
  /**
   * This is the unit of measure for the application rate. Should always be
   * in imperial units and be derivable from the type.
   */
  unit: z.string()
})

export const regionSchema = featureSchema({
  geometry: z.discriminatedUnion('type', [polygonSchema, multiPolygonSchema]),
  properties: z.record(z.string(), z.unknown()).nullable()
})

export const regionCollectionSchema = featureCollectionSchema(regionSchema)

export type RegionCollection = z.infer<typeof regionCollectionSchema>

export const boundarySchema = z.object({
  /**
   * The encompassing boundary of the field, which may contain holes.
   */
  boundary: z.discriminatedUnion('type', [regionSchema, regionCollectionSchema]),
  /**
   * If true, the area inside the boundary is excluded, and the area outside
   * the boundary is included. If false, the area inside the boundary is
   * included, and the area outside the boundary is excluded.
   */
  inverted: z.boolean().default(false)
})

/**
 * A simplified version of a boundary file, with fewer vertices
 * so that it is more efficient to send over the wire and display
 * on the map alongside other boundaries. Also useful for efficient
 * hit detection.
 *
 * This is the "canonical" approximation in that there is only one
 * per field, unlike boundary files in general, of which there may
 * be multiple per field. `boundaryId` names the boundary asset it was
 * derived from — the field's default boundary — so that the approximation
 * can be reattributed or dropped when that asset is deleted.
 *
 * Only the outline is taken from `boundarySchema`, not its `inverted` flag: an
 * inverted boundary carves an exclusion out of a field rather than describing
 * its extent, so it is never eligible to be a field's default.
 */
export const canonicalFieldBoundaryApproximationSchema = z.object({
  orgCode: z.string(),
  fieldId: z.string(),
  boundaryId: z.string(),
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  centroid: z.tuple([z.number(), z.number()]),
  acres: z.number(),
  boundary: boundarySchema.shape.boundary
})

/**
 * The structure derived from a field boundary by Helm's guidance inspection:
 * a heterogeneous feature collection where Polygons describe guidance regions
 * (rings) and their holes, LineStrings describe the edges between regions, and
 * Points describe the corners that connect those edges. Every feature carries an
 * `id` (and usually a `group`) in its properties; a plan's guidance overrides and
 * infill regions reference those ids. Helm mints the ids afresh on every
 * inspection, so an id means something only alongside the collection it came in.
 */
export const guidanceFieldFeatureSchema = featureSchema({
  geometry: z.discriminatedUnion('type', [polygonSchema, lineStringSchema, pointSchema]),
  // Helm places the id inside `properties.id` rather than at the
  // feature's top level; plan overrides and infill regions reference that id,
  // so the parse boundary must guarantee every feature carries one. Not
  // `.nullable()` because the id invariant is precisely what this schema
  // exists to enforce — Helm's contract is that inspection features always
  // emit a non-null properties object with an id, and a `null` here signals
  // a real upstream bug we want the parse to surface, not silently coerce.
  properties: z.object({ id: z.string() }).catchall(z.unknown())
})

export const guidanceFieldFeaturesSchema = featureCollectionSchema(guidanceFieldFeatureSchema)

export type GuidanceFieldFeatures = z.infer<typeof guidanceFieldFeaturesSchema>

export const enrichedFieldBoundaryCollectionSchema = featureCollectionSchema(
  featureSchema({
    geometry: z.discriminatedUnion('type', [polygonSchema, multiPolygonSchema]),
    properties: z.object({
      planningFieldId: z.string(),
      client: z.string(),
      farm: z.string(),
      field: z.string(),
      centroid: z.tuple([z.number(), z.number()]),
      acres: z.number()
    })
  })
)

export type EnrichedFieldBoundaryFeature = z.infer<
  typeof enrichedFieldBoundaryCollectionSchema
>['features'][number]

export type EnrichedFieldBoundaryProperties = EnrichedFieldBoundaryFeature['properties']
